import { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from '../hooks/useTranslation';
import { PasswordInput } from '../components/common/PasswordInput';

/**
 * PasswordResetPage Component
 * 
 * Password reset page where users enter their new password.
 * Receives token from URL query parameter (?token=xxx).
 * 
 * Features:
 * - New password input with validation
 * - Password confirmation field
 * - Password requirements display
 * - Token validation
 * - Success/error handling
 * 
 * @validates Requirements 5.7, 7.5
 */
export function PasswordResetPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Password validation state
  const [passwordChecks, setPasswordChecks] = useState({
    length: false,
    hasLetter: false,
    hasNumber: false,
    hasUppercase: false,
    hasSpecial: false,
  });

  // Update password validation checks
  useEffect(() => {
    setPasswordChecks({
      length: password.length >= 8 && password.length <= 128,
      hasLetter: /[a-zA-Z]/.test(password),
      hasNumber: /\d/.test(password),
      hasUppercase: /[A-Z]/.test(password),
      hasSpecial: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?`~]/.test(password),
    });
  }, [password]);

  /**
   * Validates password meets requirements:
   * - 8-128 characters
   * - At least one letter
   * - At least one number
   * - At least one uppercase letter
   * - At least one special character
   */
  const isValidPassword = (): boolean => {
    return passwordChecks.length && passwordChecks.hasLetter && passwordChecks.hasNumber && passwordChecks.hasUppercase && passwordChecks.hasSpecial;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validate passwords match
    if (password !== confirmPassword) {
      setError(t('passwordReset.reset.errors.noMatch'));
      return;
    }

    // Validate password requirements
    if (!isValidPassword()) {
      setError(t('passwordReset.reset.errors.notMeetRequirements'));
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch('/api/auth/password/reset', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || t('passwordReset.reset.errors.generic'));
      }

      setIsSuccess(true);
    } catch (err) {
      if (err instanceof Error) {
        // Handle specific error cases
        if (err.message.includes('expiré') || err.message.includes('expired')) {
          setError(t('passwordReset.reset.errors.expired'));
        } else if (err.message.includes('invalide') || err.message.includes('invalid')) {
          setError(t('passwordReset.reset.errors.invalid'));
        } else {
          setError(err.message);
        }
      } else {
        setError(t('passwordReset.reset.errors.generic'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  // No token provided - show error state
  if (!token) {
    return (
      <div className="max-w-md mx-auto py-8">
        <div className="text-center space-y-4 bg-white dark:bg-slate-800 p-6 md:p-8 rounded-2xl shadow-sm border border-slate-100 dark:border-slate-700">
          {/* Error icon */}
          <div className="w-16 h-16 mx-auto bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
            <svg
              className="w-8 h-8 text-red-600 dark:text-red-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>

          <h1 className="font-display text-2xl font-bold text-slate-900 dark:text-white">
            {t('passwordReset.reset.invalidLink.title')}
          </h1>

          <p className="text-slate-600 dark:text-slate-400">
            {t('passwordReset.reset.invalidLink.message')}
          </p>

          <div className="pt-4">
            <Link to="/password-reset-request" className="block w-full btn-primary">
              {t('passwordReset.reset.invalidLink.requestNew')}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Success state after password reset
  if (isSuccess) {
    return (
      <div className="max-w-md mx-auto py-8">
        <div className="text-center space-y-4 bg-white dark:bg-slate-800 p-6 md:p-8 rounded-2xl shadow-sm border border-slate-100 dark:border-slate-700">
          {/* Success icon */}
          <div className="w-16 h-16 mx-auto bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
            <svg
              className="w-8 h-8 text-green-600 dark:text-green-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>

          <h1 className="font-display text-2xl font-bold text-slate-900 dark:text-white">
            {t('passwordReset.reset.success.title')}
          </h1>

          <p className="text-slate-600 dark:text-slate-400">
            {t('passwordReset.reset.success.message')}
          </p>

          <div className="pt-4">
            <button
              onClick={() => navigate('/login')}
              className="block w-full btn-primary"
            >
              {t('passwordReset.reset.success.login')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto py-8">
      <div className="text-center space-y-2 mb-8">
        <h1 className="font-display text-3xl font-bold text-slate-900 dark:text-white">
          {t('passwordReset.reset.title')}
        </h1>
        <p className="text-slate-600 dark:text-slate-400">
          {t('passwordReset.reset.subtitle')}
        </p>
      </div>

      <form
        onSubmit={handleSubmit}
        className="space-y-6 bg-white dark:bg-slate-800 p-6 md:p-8 rounded-2xl shadow-sm border border-slate-100 dark:border-slate-700"
      >
        {/* Error message */}
        {error && (
          <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl">
            <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
            {(error.includes('expiré') || error.includes('invalide') || error.includes('expired') || error.includes('invalid')) && (
              <Link
                to="/password-reset-request"
                className="inline-block mt-2 text-sm text-red-700 dark:text-red-400 font-medium underline hover:no-underline"
              >
                {t('passwordReset.reset.errors.requestNewLink')}
              </Link>
            )}
          </div>
        )}

        {/* New password field */}
        <div className="space-y-2">
          <label htmlFor="password" className="block text-sm font-medium text-slate-700 dark:text-slate-300">
            {t('passwordReset.reset.password.label')}
          </label>
          <PasswordInput
            id="password"
            name="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
            placeholder={t('passwordReset.reset.password.placeholder')}
            required
            disabled={isLoading}
          />
          
          {/* Password requirements - Simple text like Budgira */}
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-2">
            {t('passwordReset.reset.password.requirements.hint')}
          </p>
        </div>

        {/* Confirm password field */}
        <div className="space-y-2">
          <label htmlFor="confirmPassword" className="block text-sm font-medium text-slate-700 dark:text-slate-300">
            {t('passwordReset.reset.confirmPassword.label')}
          </label>
          <PasswordInput
            id="confirmPassword"
            name="confirmPassword"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            autoComplete="new-password"
            placeholder={t('passwordReset.reset.confirmPassword.placeholder')}
            required
            disabled={isLoading}
          />
          {confirmPassword && password !== confirmPassword && (
            <p className="text-xs text-red-600 dark:text-red-400">
              {t('passwordReset.reset.confirmPassword.noMatch')}
            </p>
          )}
          {confirmPassword && password === confirmPassword && password.length > 0 && (
            <p className="text-xs text-green-600 dark:text-green-400 flex items-center gap-1">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              {t('passwordReset.reset.confirmPassword.match')}
            </p>
          )}
        </div>

        {/* Submit button */}
        <button
          type="submit"
          disabled={isLoading || !isValidPassword() || password !== confirmPassword}
          className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading ? (
            <span className="flex items-center justify-center gap-2">
              <svg
                className="animate-spin h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              {t('passwordReset.reset.submitting')}
            </span>
          ) : (
            t('passwordReset.reset.submit')
          )}
        </button>
      </form>

      {/* Back to login link */}
      <p className="text-center mt-6 text-slate-600 dark:text-slate-400">
        <Link to="/login" className="font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300">
          {t('passwordReset.reset.backToLogin')}
        </Link>
      </p>
    </div>
  );
}
